import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Link from "@cloudscape-design/components/link"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { usePageParam } from "@/hooks/usePageParam"
import type { DTARecord } from "../../../services/dtaApi"
import Pagination from "../../../components/Pagination/Pagination"
import { getDTADisplayName } from "../../../components/shared/dtaDisplay"
import InlineActionButton from "./InlineActionButton"

const PAGE_SIZE = 20

type BadgeColor = React.ComponentProps<typeof Badge>["color"]

const STATUS_COLOR: Record<string, BadgeColor> = {
  DRAFT: "grey",
  REVIEW: "blue",
  APPROVED: "green",
  ACTIVE: "green",
  INACTIVE: "red",
}

const sourceLabel = (dta: DTARecord) =>
  dta.source_type === "edc"
    ? "EDC"
    : dta.source_type === "sdtm"
      ? "SDTM"
      : `Third-party (${dta.vendor_name || dta.vendor_id})`

interface DtaTableProps {
  /**
   * All the study's DTAs, sorted, filtered, and paginated client-side.
   */
  dtas: DTARecord[]
  loading: boolean
  /**
   * Header count, which may exceed `dtas.length` while pages are still loading.
   */
  totalCount: number
  /**
   * Whether more pages are loading; adds a "+" after the count.
   */
  fetchingMore: boolean
  /**
   * Header row actions, e.g. "Create DTA".
   */
  actions?: React.ReactNode
  onAmend: (dta: DTARecord) => void
}

/**
 * Table of a study's DTAs, newest first, for the Overview and DTAs tabs. Amend is gated by the
 * caller's experience and the DTA's stage.
 */
const DtaTable = ({ dtas, loading, totalCount, fetchingMore, actions, onAmend }: DtaTableProps) => {
  const navigate = useNavigate()
  const { isInternal: isDAE } = useMyAccess()

  const [filterText, setFilterText] = useState("")
  const [currentPage, setCurrentPage] = usePageParam("dtaPage")
  const [sorting, setSorting] = useState<TableProps.SortingState<DTARecord>>({
    sortingColumn: { sortingField: "created_at" },
    isDescending: true,
  })

  const columnDefinitions: TableProps.ColumnDefinition<DTARecord>[] = useMemo(
    () => [
      {
        id: "domain",
        header: "Domain",
        sortingField: "domains",
        sortingComparator: (a, b) => (a.domains?.[0] ?? "").localeCompare(b.domains?.[0] ?? ""),
        cell: (dta) => (
          <Link
            href={`/dtas/${dta.dta_id}`}
            onFollow={(e) => {
              e.preventDefault()
              navigate(`/dtas/${dta.dta_id}`)
            }}
          >
            {dta.domains?.[0] || getDTADisplayName(dta) || "Untitled"}
          </Link>
        ),
      },
      {
        id: "source",
        header: "Source",
        sortingComparator: (a, b) => sourceLabel(a).localeCompare(sourceLabel(b)),
        cell: sourceLabel,
      },
      {
        id: "version",
        header: "Version",
        sortingField: "version",
        cell: (dta) => dta.version || "0.0",
      },
      {
        id: "status",
        header: "Status",
        sortingField: "workflow_stage",
        cell: (dta) => (
          <Badge color={STATUS_COLOR[dta.workflow_stage] ?? "grey"}>
            {dta.workflow_stage.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        id: "created",
        header: "Created",
        sortingField: "created_at",
        sortingComparator: (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        cell: (dta) =>
          new Date(dta.created_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
      },
      {
        id: "actions",
        header: "Actions",
        minWidth: 200,
        cell: (dta) => (
          <span className="dta-table-actions">
            <InlineActionButton onClick={() => navigate(`/dtas/${dta.dta_id}`)}>
              View DTA
            </InlineActionButton>
            <span className="dta-table-actions-divider" aria-hidden="true">
              |
            </span>
            <InlineActionButton
              disabled={!isDAE || dta.workflow_stage === "DRAFT"}
              disabledReason={
                !isDAE ? "Only DAE users can amend a DTA" : "This DTA is a draft - edit it directly"
              }
              onClick={() => onAmend(dta)}
            >
              Amend DTA
            </InlineActionButton>
          </span>
        ),
      },
    ],
    [navigate, isDAE, onAmend],
  )

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase()
    if (!q) return dtas
    return dtas.filter(
      (d) =>
        (d.domains?.[0] ?? "").toLowerCase().includes(q) ||
        sourceLabel(d).toLowerCase().includes(q) ||
        d.workflow_stage.toLowerCase().includes(q) ||
        (d.vendor_name ?? "").toLowerCase().includes(q),
    )
  }, [dtas, filterText])

  const sorted = useMemo(() => {
    const { sortingColumn, isDescending } = sorting
    const comparator = sortingColumn.sortingComparator
    const field = sortingColumn.sortingField as keyof DTARecord | undefined
    const items = [...filtered].sort((a, b) => {
      if (comparator) return comparator(a, b)
      if (!field) return 0
      return String(a[field] ?? "").localeCompare(String(b[field] ?? ""))
    })
    return isDescending ? items.reverse() : items
  }, [filtered, sorting])

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const clampedPage = Math.min(currentPage, pageCount)
  const pageItems = sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE)

  return (
    <Container
      header={
        <Header counter={`(${totalCount}${fetchingMore ? "+" : ""})`} actions={actions}>
          Data Transfer Agreements
        </Header>
      }
    >
      <Table
        variant="embedded"
        columnDefinitions={columnDefinitions}
        items={pageItems}
        loading={loading}
        loadingText="Loading DTAs..."
        trackBy="dta_id"
        sortingColumn={sorting.sortingColumn}
        sortingDescending={sorting.isDescending}
        onSortingChange={({ detail }) => setSorting(detail)}
        filter={
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Search by domain, source, vendor, or status..."
            filteringAriaLabel="Search DTAs"
            onChange={({ detail }) => {
              setFilterText(detail.filteringText)
              setCurrentPage(1)
            }}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {filterText ? "No DTAs match your search." : "No DTAs yet."}
          </Box>
        }
        pagination={
          <Pagination
            currentPage={clampedPage}
            pageCount={pageCount}
            hasMore={false}
            loading={loading}
            onNext={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
            onPrevious={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            onPageChange={setCurrentPage}
          />
        }
      />
    </Container>
  )
}

export default DtaTable
